/**
 * StoreSetupPage Component
 * 
 * Comprehensive store configuration page with tabs for:
 * - Branding (logo, name, contact info)
 * - Catering Settings (default values, pricing)
 * - Menu Management (bulk import/export)
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.currentUser - Current logged-in user
 * @param {Object} props.currentStore - Current store data
 * @param {Function} props.onBack - Callback to navigate back
 * @param {Function} props.onStoreUpdate - Callback when store data is updated
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Home, Store, Download, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';
import { getImageUrl } from '../../utils/formatters';

function StoreSetupPage({ currentUser, currentStore, onBack, onStoreUpdate }) {
  const [activeTab, setActiveTab] = useState('branding'); // 'branding', 'catering', 'menu'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Branding state
  const [storeName, setStoreName] = useState(currentStore?.name || '');
  const [storePhone, setStorePhone] = useState(currentStore?.phone || '');
  const [storeAddress, setStoreAddress] = useState(currentStore?.address || '');
  const [tagline, setTagline] = useState(currentStore?.tagline || '');
  const [logoFile, setLogoFile] = useState(null);
 
  // Excel import state
  const [excelFile, setExcelFile] = useState(null);
  const [previewItems, setPreviewItems] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [aiMapping, setAIMapping] = useState(null);
  const [showAIMapping, setShowAIMapping] = useState(false);
  const [editableMappings, setEditableMappings] = useState({});
  const [showMappingEditor, setShowMappingEditor] = useState(false);
  const [userExcelColumns, setUserExcelColumns] = useState([]);
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setLogoFile(file);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setSaving(true);
      const storeId = currentStore?.storeId || currentStore?.id;
      const response = await axios.post(`${API}/stores/${storeId}/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Logo uploaded successfully!');
      // Update current store
      const updatedStore = {...currentStore, logoUrl: response.data.logoUrl};
      onStoreUpdate(updatedStore);
    } catch (error) {
      console.error('Logo upload failed:', error);
      toast.error('Failed to upload logo');
    } finally {
      setSaving(false);
    }
  };
  
 
  
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const storeId = currentStore?.storeId || currentStore?.id;
      
      // Send full store object with updated fields
      const updatedStoreData = {
        ...currentStore,
        name: storeName,
        phone: storePhone,
        address: storeAddress,
        tagline
      };
      
      await axios.put(`${API}/stores/${storeId}`, updatedStoreData, {
        headers: {
          'X-User-Role': currentUser?.role || 'store_admin'
        }
      });
      toast.success('Store settings saved successfully!');
      
      // Update current store
      onStoreUpdate(updatedStoreData);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }
    
    console.log('File selected:', file.name, 'Size:', file.size, 'Type:', file.type);
    
    setExcelFile(file);
    setShowPreview(false); // Reset preview state
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setLoading(true);
      const storeId = currentStore?.storeId || currentStore?.id;
      console.log('Uploading to store:', storeId);
      
      const uploadUrl = `${API}/menu/upload-excel?store_id=${storeId}`;
      console.log('Upload URL:', uploadUrl);
      
      const response = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      console.log('Excel upload SUCCESS - Full response:', response);
      console.log('Response data:', response.data);
      console.log('Items count:', response.data?.items?.length);
      console.log('First item sample:', response.data?.items?.[0]);
      
      if (response.data && response.data.items && response.data.items.length > 0) {
        console.log('Setting preview items:', response.data.items);
        console.log('Sample TRAY item (first):', {
          name: response.data.items[0]?.name,
          uom: response.data.items[0]?.unit_of_measure,
          pricing: response.data.items[0]?.pricing
        });
        
        // Find a non-tray item to check pricing
        const nonTrayItem = response.data.items.find(item => 
          !item.unit_of_measure?.toLowerCase().includes('tray')
        );
        if (nonTrayItem) {
          console.log('Sample NON-TRAY item:', {
            name: nonTrayItem.name,
            uom: nonTrayItem.unit_of_measure,
            pricing: nonTrayItem.pricing
          });
        }
        
        setPreviewItems(response.data.items);
        setShowPreview(true);
        console.log('Preview should now be visible');
        toast.success(`✓ Parsed ${response.data.count} items from Excel. Review and import below.`);
      } else {
        console.error('No items in response or items array is empty');
        toast.error('No items found in Excel file. Please check the format.');
      }
    } catch (error) {
      console.error('Excel upload FAILED - Full error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to parse Excel file';
      toast.error(`Upload Error: ${errorMsg}`);
    } finally {
      setLoading(false);
      console.log('Loading state set to false');
    }
  };
  
  const handleAIExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setExcelFile(file);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setLoading(true);
      const storeId = currentStore?.storeId || currentStore?.id;
      
      toast.info('AI is analyzing your Excel file...');
      
      const response = await axios.post(`${API}/menu/ai-convert-excel?store_id=${storeId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Store AI mapping details
      setAIMapping({
        mapping: response.data.mapping,
        inferred_logic: response.data.inferred_logic,
        raw_data: response.data.items // Store raw converted items
      });
      
      // Extract user's Excel columns
      const userColumns = Object.keys(response.data.mapping || {});
      setUserExcelColumns(userColumns);
      
      // Initialize editable mappings from AI suggestions
      const initialMappings = {};
      Object.entries(response.data.mapping || {}).forEach(([userCol, mapping]) => {
        initialMappings[userCol] = mapping.platform_field;
      });
      setEditableMappings(initialMappings);
      
      // Show mapping editor first (new step)
      setShowMappingEditor(true);
      
      toast.success(`✨ AI analyzed your Excel! Please review and adjust the column mappings.`);
    } catch (error) {
      console.error('AI Excel conversion failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to convert Excel file';
      toast.error(`AI Conversion Failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleConfirmMappings = async () => {
    try {
      setLoading(true);
      
      // Here we would re-convert the Excel with the user's confirmed mappings
      // For now, we'll use the AI's original conversion but in production
      // you'd want to call the backend again with the adjusted mappings
      
      setPreviewItems(aiMapping.raw_data);
      setShowMappingEditor(false);
      setShowAIMapping(true);
      setShowPreview(true);
      
      toast.success(`Converted ${aiMapping.raw_data.length} items with your mappings!`);
    } catch (error) {
      console.error('Failed to apply mappings:', error);
      toast.error('Failed to apply mappings');
    } finally {
      setLoading(false);
    }
  };
  
  const handleImportItems = async () => {
    try {
      setLoading(true);
      const storeId = currentStore?.storeId || currentStore?.id;
      await axios.post(`${API}/menu/import-items?store_id=${storeId}`, previewItems);
      
      toast.success(`Successfully imported ${previewItems.length} menu items! Go to Menu Management to verify.`, { duration: 5000 });
      setShowPreview(false);
      setPreviewItems([]);
      setExcelFile(null);
      setShowAIMapping(false);
      setAIMapping(null);
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import items');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadTemplate = () => {
    window.open(`${API}/menu/download-template`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <div className="nav-bawarchi">
        <div className="nav-brand flex items-center gap-2">
          {currentStore?.logoUrl && (
            <img src={getImageUrl(currentStore.logoUrl)} alt="Store Logo" className="h-16 w-auto" />
          )}
          <div className="flex flex-col">
            <span className="font-semibold">{currentStore?.name || 'CaterLog'}</span>
            {currentStore?.tagline && (
              <span className="text-sm text-gray-600">{currentStore.tagline}</span>
            )}
          </div>
        </div>
        <button onClick={onBack} className="btn-bawarchi-secondary" style={{ padding: '8px 20px', minHeight: '40px' }}>
          <Home className="w-4 h-4 mr-2 inline" />
          Back to Dashboard
        </button>
      </div>

      <div className="page-container">
        <div className="page-header text-center">
          <h1 className="page-title">Store Setup</h1>
          <p className="page-subtitle">{currentStore?.name} - Configure your store</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('branding')}
              className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'branding' 
                  ? 'text-orange-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
              style={activeTab === 'branding' ? {borderBottomColor: currentStore?.primaryColor || '#EA580C'} : {}}
            >
              <Store className="w-4 h-4 inline mr-2" />
              Branding
            </button>
            <button
              onClick={() => setActiveTab('catering')}
              className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'catering' 
                  ? 'text-orange-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
              style={activeTab === 'catering' ? {borderBottomColor: currentStore?.primaryColor || '#EA580C'} : {}}
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              Catering Settings
            </button>
            <button
              onClick={() => setActiveTab('menu')}
              className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'menu' 
                  ? 'text-orange-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
              style={activeTab === 'menu' ? {borderBottomColor: currentStore?.primaryColor || '#EA580C'} : {}}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Menu Management
            </button>
          </div>
        </div>

        {/* Branding Tab */}
        {activeTab === 'branding' && (
          <Card>
            <CardHeader>
              <CardTitle>Store Information & Branding</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label-bawarchi">Store Name *</label>
                  <input
                    type="text"
                    className="input-bawarchi"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    placeholder="Store Name that appears on invoices"
                  />
                </div>
                
                <div>
                  <label className="label-bawarchi">Phone Number *</label>
                  <input
                    type="tel"
                    className="input-bawarchi"
                    value={storePhone}
                    onChange={(e) => setStorePhone(e.target.value)}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>
              
              <div>
                <label className="label-bawarchi">Address *</label>
                <input
                  type="text"
                  className="input-bawarchi"
                  value={storeAddress}
                  onChange={(e) => setStoreAddress(e.target.value)}
                  placeholder="Enter store address"
                />
              </div>
              
              <div>
                <label className="label-bawarchi">Store Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="input-bawarchi"
                  disabled={saving}
                />
                {currentStore?.logoUrl && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Current Logo:</p>
                    <img src={getImageUrl(currentStore.logoUrl)} alt="Store Logo" className="h-24 rounded border border-gray-200 p-2" />
                  </div>
                )}
              </div>
              
              <div>
                <label className="label-bawarchi">Tagline (Optional)</label>
                <input
                  type="text"
                  className="input-bawarchi"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="e.g., Authentic Indian Catering"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleSaveSettings}
                  className="btn-bawarchi-secondary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  onClick={async () => {
                    await handleSaveSettings();
                    if (!saving) {
                      setActiveTab('menu');
                      toast.success('Proceed to Menu Import');
                    }
                  }}
                  className="btn-bawarchi-primary"
                  disabled={saving}
                >
                  Save & Continue to Menu Import
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Catering Settings Tab */}
        {activeTab === 'catering' && (
          <Card>
            <CardHeader>
              <CardTitle>Catering Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Default Catering Settings</h4>
                <p className="text-sm text-blue-800 mb-4">Configure default values for new orders</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label-bawarchi">Default Service Fee (%)</label>
                    <select className="input-bawarchi">
                      <option value="0">No Service Fee</option>
                      <option value="5">5%</option>
                      <option value="10">10%</option>
                      <option value="12">12%</option>
                      <option value="15">15%</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="label-bawarchi">Default Catering Type</label>
                    <select className="input-bawarchi">
                      <option value="pickup">Pick up</option>
                      <option value="delivery">Delivery Only</option>
                      <option value="delivery_setup">Delivery & Setup</option>
                      <option value="live">Live Catering</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Pricing & Discounts</h4>
                <p className="text-sm text-green-800 mb-4">Configure default pricing options</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label-bawarchi">Default Discount Type</label>
                    <select className="input-bawarchi">
                      <option value="none">No Discount</option>
                      <option value="percentage">Percentage Discount</option>
                      <option value="cash_discount">Fixed Amount Discount</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="label-bawarchi">Minimum Order Amount</label>
                    <input
                      type="number"
                      className="input-bawarchi"
                      placeholder="Enter minimum order amount"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  className="btn-bawarchi-secondary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Catering Settings'}
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Menu Management Tab */}
        {activeTab === 'menu' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Excel Menu Import</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Step 1: Download Template */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Step 1: Download Template</h4>
                    <p className="text-xs text-blue-800 mb-3">Download Excel template with the correct format.</p>
                    <button
                      onClick={downloadTemplate}
                      className="btn-bawarchi-primary flex items-center gap-2"
                      disabled={loading}
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                    >
                      <Download className="w-4 h-4" />
                      Download Template
                    </button>
                  </div>
                  
                  {/* Step 2: Upload Menu */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">Step 2: Upload Your Menu</h4>
                    <p className="text-xs text-green-800 mb-3">Fill template with menu items & upload here.</p>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelUpload}
                      className="input-bawarchi"
                      disabled={loading}
                      key={excelFile ? excelFile.name : 'file-input'}
                    />
                    {loading && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-green-700">
                        <div className="inline-block w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                        Processing your Excel file...
                      </div>
                    )}
                    {excelFile && !loading && !showPreview && (
                      <p className="mt-3 text-sm text-green-700">✓ File selected: {excelFile.name}</p>
                    )}
                    {excelFile && !loading && showPreview && (
                      <p className="mt-3 text-sm text-green-700">✓ File processed - scroll down to see preview</p>
                    )}
                </div>
                </div>
                
                {/* Debug Info */}
                {excelFile && !loading && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs space-y-2">
                    <p><strong>🔍 Debug Info:</strong></p>
                    <p>• File: {excelFile.name}</p>
                    <p>• Show Preview: {showPreview ? '✅ Yes' : '❌ No'}</p>
                    <p>• Preview Items Count: {previewItems.length}</p>
                    {!showPreview && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                        <p className="text-red-700 font-semibold">⚠️ Preview Not Showing</p>
                        <p className="text-red-600 mt-1">Possible issues:</p>
                        <ul className="list-disc list-inside text-red-600 ml-2">
                          <li>Upload failed silently</li>
                          <li>Backend returned 0 items</li>
                          <li>Check browser console (F12 → Console tab) for errors</li>
                        </ul>
                        <button
                          onClick={() => {
                            console.log('=== MANUAL DEBUG TEST ===');
                            console.log('Current State:');
                            console.log('- showPreview:', showPreview);
                            console.log('- previewItems:', previewItems);
                            console.log('- excelFile:', excelFile);
                            alert(`Debug:\nshowPreview: ${showPreview}\npreviewItems.length: ${previewItems.length}\n\nCheck console for full details`);
                          }}
                          className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                        >
                          Show Debug Info in Console
                        </button>
                      </div>
                    )}
                    {showPreview && previewItems.length === 0 && (
                      <p className="text-orange-600 font-semibold">⚠️ Preview enabled but 0 items parsed from Excel</p>
                    )}
                  </div>
                )}
                
                {/* AI-Powered Conversion temporarily hidden - reserved for future premium version */}
                {/*
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 opacity-60 cursor-not-allowed">
                  <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    AI-Powered Conversion (Coming Soon)
                  </h4>
                  <p className="text-sm text-purple-800 mb-1">
                    AI-powered Excel import will be available in a future Premium version.
                  </p>
                  <p className="text-xs text-purple-700 italic">For now, please use the standard template upload above.</p>
                </div>
                */}
              </CardContent>
            </Card>
            
            {/* Mapping Editor - Step 3: User Adjustable Mappings */}
            {showMappingEditor && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-900">
                    <Sparkles className="w-5 h-5" />
                    Step 3: Review & Adjust Column Mappings
                  </CardTitle>
                  <p className="text-sm text-blue-700 mt-2">
                    AI has suggested mappings below. Please review and adjust if needed before converting your data.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-4">Map Your Excel Columns to Platform Fields:</h4>
                    <div className="space-y-3">
                      {userExcelColumns.map((userCol) => (
                        <div key={userCol} className="grid grid-cols-12 gap-3 items-center p-3 bg-blue-50 rounded-lg">
                          {/* User's Column Name */}
                          <div className="col-span-5">
                            <Label className="text-xs text-blue-700 mb-1 block">Your Excel Column:</Label>
                            <div className="font-medium text-blue-900 bg-white p-2 rounded border border-blue-200">
                              {userCol}
                            </div>
                          </div>
                          
                          {/* Arrow */}
                          <div className="col-span-1 text-center text-blue-600 text-2xl">
                            →
                          </div>
                          
                          {/* Platform Field Dropdown */}
                          <div className="col-span-6">
                            <Label className="text-xs text-blue-700 mb-1 block">Maps to Platform Field:</Label>
                            <Select 
                              value={editableMappings[userCol] || ''} 
                              onValueChange={(value) => setEditableMappings({...editableMappings, [userCol]: value})}
                            >
                              <SelectTrigger className="border-blue-300 bg-white">
                                <SelectValue placeholder="Select platform field..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="category">Category</SelectItem>
                                <SelectItem value="name">Item Name</SelectItem>
                                <SelectItem value="description">Description</SelectItem>
                                <SelectItem value="protein_type">Protein Type</SelectItem>
                                <SelectItem value="dietary_flags">Dietary Flags</SelectItem>
                                <SelectItem value="spice_level">Spice Level</SelectItem>
                                <SelectItem value="unit_of_measure">UOM (Unit of Measure)</SelectItem>
                                <SelectItem value="serves">Serves</SelectItem>
                                <SelectItem value="base_price">Base Price</SelectItem>
                                <SelectItem value="quarter_tray">Quarter Tray Price</SelectItem>
                                <SelectItem value="half_tray">Half Tray Price</SelectItem>
                                <SelectItem value="full_tray">Full Tray Price</SelectItem>
                                <SelectItem value="large_tray">Large Tray Price</SelectItem>
                                <SelectItem value="additional_sizes">Additional Sizes</SelectItem>
                                <SelectItem value="notes">Notes</SelectItem>
                                <SelectItem value="is_active">Availability</SelectItem>
                                <SelectItem value="skip">Skip (Don't Import)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-blue-100 border border-blue-300 rounded-lg p-4 text-sm text-blue-900">
                    <p className="font-semibold mb-2">📋 Quick Tips:</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-800">
                      <li>Review each mapping carefully before proceeding</li>
                      <li>Select "Skip" for columns you don't want to import</li>
                      <li>Price columns should map to specific tray sizes</li>
                      <li>After confirming, you'll see a preview of converted data</li>
                    </ul>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleConfirmMappings}
                      className="btn-bawarchi-primary"
                      disabled={loading}
                    >
                      {loading ? 'Converting...' : 'Confirm Mappings & Preview Data'}
                    </button>
                    <button
                      onClick={() => {
                        setShowMappingEditor(false);
                        setEditableMappings({});
                        setAIMapping(null);
                      }}
                      className="btn-bawarchi-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* AI Mapping Results */}
            {showAIMapping && aiMapping && (
              <Card className="border-purple-200 bg-purple-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-900">
                    <Sparkles className="w-5 h-5" />
                    AI Column Mapping Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <h4 className="font-semibold text-purple-900 mb-3">Column Mappings:</h4>
                    <div className="space-y-2 text-sm">
                      {Object.entries(aiMapping.mapping || {}).map(([userCol, mapping]) => (
                        <div key={userCol} className="flex items-start gap-3 p-2 bg-purple-50 rounded">
                          <div className="flex-1">
                            <span className="font-medium text-purple-900">{userCol}</span>
                            <span className="text-gray-600 mx-2">→</span>
                            <span className="text-purple-700">{mapping.platform_field}</span>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            mapping.confidence === 'high' ? 'bg-green-100 text-green-700' :
                            mapping.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {mapping.confidence}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {aiMapping.inferred_logic && Object.keys(aiMapping.inferred_logic).length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <h4 className="font-semibold text-purple-900 mb-3">AI Inferred Fields:</h4>
                      <div className="space-y-2 text-sm">
                        {Object.entries(aiMapping.inferred_logic).map(([field, logic]) => (
                          <div key={field} className="p-2 bg-purple-50 rounded">
                            <span className="font-medium text-purple-900">{field}: </span>
                            <span className="text-gray-700">{logic}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-sm text-purple-900">
                    <strong>Note:</strong> Please review the mapped data below. You can import directly if everything looks correct, or adjust your Excel file and try again.
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Preview */}
            {showPreview && previewItems.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Preview Items ({previewItems.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-gray-100">
                          <th className="text-left p-2 min-w-[100px]">Category</th>
                          <th className="text-left p-2 min-w-[150px]">Item Name</th>
                          <th className="text-left p-2 min-w-[100px]">Protein</th>
                          <th className="text-left p-2 min-w-[100px]">UOM</th>
                          <th className="text-center p-2 min-w-[60px]">QT</th>
                          <th className="text-center p-2 min-w-[60px]">MHT</th>
                          <th className="text-center p-2 min-w-[60px]">HT</th>
                          <th className="text-center p-2 min-w-[60px]">MLT</th>
                          <th className="text-center p-2 min-w-[60px]">LT</th>
                          <th className="text-center p-2 min-w-[70px]">Base Price</th>
                          <th className="text-left p-2 min-w-[120px]">Notes</th>
                          <th className="text-center p-2">Active</th>
                          <th className="text-center p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewItems.map((item, idx) => {
                          const isTray = item.unit_of_measure?.toLowerCase().includes('tray');
                          return (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-2">
                                <Input
                                  value={item.category || ''}
                                  onChange={(e) => {
                                    const updated = [...previewItems];
                                    updated[idx] = { ...updated[idx], category: e.target.value };
                                    setPreviewItems(updated);
                                  }}
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  value={item.name || ''}
                                  onChange={(e) => {
                                    const updated = [...previewItems];
                                    updated[idx] = { ...updated[idx], name: e.target.value };
                                    setPreviewItems(updated);
                                  }}
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  value={item.protein_type || ''}
                                  onChange={(e) => {
                                    const updated = [...previewItems];
                                    updated[idx] = { ...updated[idx], protein_type: e.target.value };
                                    setPreviewItems(updated);
                                  }}
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="p-2">
                                <select
                                  className="h-8 text-xs border rounded w-full"
                                  value={item.unit_of_measure || ''}
                                  onChange={(e) => {
                                    const updated = [...previewItems];
                                    updated[idx] = { ...updated[idx], unit_of_measure: e.target.value };
                                    setPreviewItems(updated);
                                  }}
                                >
                                  <option value="">Select UOM</option>
                                  <option value="Per Tray">Per Tray</option>
                                  <option value="Per Piece">Per Piece</option>
                                  <option value="Per Order">Per Order</option>
                                  <option value="Custom">Custom</option>
                                </select>
                              </td>
                              {/* Tray Size Prices */}
                              <td className="p-2">
                                {isTray && (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.pricing?.qt || ''}
                                    onChange={(e) => {
                                      const updated = [...previewItems];
                                      const pricing = { ...(updated[idx].pricing || {}) };
                                      pricing.qt = parseFloat(e.target.value) || 0;
                                      updated[idx] = { ...updated[idx], pricing };
                                      setPreviewItems(updated);
                                    }}
                                    className="h-8 text-xs w-16"
                                    placeholder="QT"
                                  />
                                )}
                              </td>
                              <td className="p-2">
                                {isTray && (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.pricing?.mht || ''}
                                    onChange={(e) => {
                                      const updated = [...previewItems];
                                      const pricing = { ...(updated[idx].pricing || {}) };
                                      pricing.mht = parseFloat(e.target.value) || 0;
                                      updated[idx] = { ...updated[idx], pricing };
                                      setPreviewItems(updated);
                                    }}
                                    className="h-8 text-xs w-16"
                                    placeholder="MHT"
                                  />
                                )}
                              </td>
                              <td className="p-2">
                                {isTray && (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.pricing?.ht || ''}
                                    onChange={(e) => {
                                      const updated = [...previewItems];
                                      const pricing = { ...(updated[idx].pricing || {}) };
                                      pricing.ht = parseFloat(e.target.value) || 0;
                                      updated[idx] = { ...updated[idx], pricing };
                                      setPreviewItems(updated);
                                    }}
                                    className="h-8 text-xs w-16"
                                    placeholder="HT"
                                  />
                                )}
                              </td>
                              <td className="p-2">
                                {isTray && (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.pricing?.mlt || ''}
                                    onChange={(e) => {
                                      const updated = [...previewItems];
                                      const pricing = { ...(updated[idx].pricing || {}) };
                                      pricing.mlt = parseFloat(e.target.value) || 0;
                                      updated[idx] = { ...updated[idx], pricing };
                                      setPreviewItems(updated);
                                    }}
                                    className="h-8 text-xs w-16"
                                    placeholder="MLT"
                                  />
                                )}
                              </td>
                              <td className="p-2">
                                {isTray && (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.pricing?.lt || ''}
                                    onChange={(e) => {
                                      const updated = [...previewItems];
                                      const pricing = { ...(updated[idx].pricing || {}) };
                                      pricing.lt = parseFloat(e.target.value) || 0;
                                      updated[idx] = { ...updated[idx], pricing };
                                      setPreviewItems(updated);
                                    }}
                                    className="h-8 text-xs w-16"
                                    placeholder="LT"
                                  />
                                )}
                              </td>
                              {/* Base Price for non-tray items */}
                              <td className="p-2">
                                {!isTray && (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.pricing?.per_piece || ''}
                                    onChange={(e) => {
                                      const updated = [...previewItems];
                                      const pricing = { ...(updated[idx].pricing || {}) };
                                      pricing.per_piece = parseFloat(e.target.value) || 0;
                                      updated[idx] = { ...updated[idx], pricing };
                                      setPreviewItems(updated);
                                    }}
                                    className="h-8 text-xs w-20"
                                    placeholder="Price"
                                  />
                                )}
                              </td>
                              <td className="p-2">
                                <Input
                                  value={item.notes || ''}
                                  onChange={(e) => {
                                    const updated = [...previewItems];
                                    updated[idx] = { ...updated[idx], notes: e.target.value };
                                    setPreviewItems(updated);
                                  }}
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={item.is_active ?? true}
                                  onChange={(e) => {
                                    const updated = [...previewItems];
                                    updated[idx] = { ...updated[idx], is_active: e.target.checked };
                                    setPreviewItems(updated);
                                  }}
                                />
                              </td>
                              <td className="p-2 text-center">
                                <button
                                  type="button"
                                  className="text-xs text-red-600 hover:underline"
                                  onClick={() => {
                                    const updated = previewItems.filter((_, i) => i !== idx);
                                    setPreviewItems(updated);
                                  }}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-4 items-center justify-center">
                    <button
                      onClick={handleImportItems}
                      className="btn-bawarchi-primary flex items-center justify-center gap-2"
                      disabled={loading}
                      style={{ padding: '10px 24px', minHeight: '44px', fontSize: '15px' }}
                    >
                      {loading && (
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      )}
                      {loading ? 'Importing...' : `✓ Import ${previewItems.length} Items to Menu`}
                    </button>
                    <button
                      onClick={() => {
                        setShowPreview(false);
                        setPreviewItems([]);
                        setExcelFile(null);
                        setShowAIMapping(false);
                        setAIMapping(null);
                        // Reset file input
                        const fileInput = document.querySelector('input[type="file"][accept=".xlsx,.xls"]');
                        if (fileInput) fileInput.value = '';
                        toast.info('Import cancelled');
                      }}
                      className="btn-bawarchi-secondary"
                      disabled={loading}
                      style={{ padding: '10px 24px', minHeight: '44px', fontSize: '15px' }}
                    >
                      Cancel Import
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        
        {/* Powered by Caterlog Footer */}
        <div className="text-center py-6 text-sm text-gray-500">
          Powered by <span className="font-semibold text-orange-600">Caterlog</span>
        </div>
      </div>
    </div>
  );
}

// Team Management Page Component (Store Admin)

export default StoreSetupPage;
